import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ShoppingCart, ChevronRight, Sun, Moon, Package } from 'lucide-react';
import type { StoreProductTier } from '@evi/shared';
import { useTheme } from '@evi/skins';
import { fetchStoreCatalog, formatCents } from '@/lib/api';
import { shopUrl } from '@/lib/shop';
import { useCartStore, selectItemCount } from '@/store/cart';
import { groupCatalog, hasChoices, TIER_LABEL, type ProductFamily } from './storeCatalog';
import { ProductOptionsSheet } from './ProductOptionsSheet';
import { EvieOrb } from '@/components/primitives';
import { useEvieChatStore } from '@/store/evieChat';
import styles from './Store.module.css';

interface Props { onNavigate: (path: string) => void }

// Customer voices. Role-only captions (no invented names or quotes); each clip
// is one of the committed UGC films with its poster as the load-in frame.
// Each card now tap-throughs to its ICP page (/store/why/:icp).
const TESTIMONIALS = [
  { src: '/media/ugc/electrician.mp4', poster: '/media/ugc/electrician-poster.jpg', role: 'Certified electrician', why: '/store/why/electrician' },
  { src: '/media/ugc/homeowner.mp4', poster: '/media/ugc/homeowner-poster.jpg', role: 'Homeowner', why: '/store/why/homeowner' },
  { src: '/media/ugc/property-manager.mp4', poster: '/media/ugc/property-manager-poster.jpg', role: 'Property manager', why: '/store/why/property-manager' },
] as const;

export function StoreCatalogPage({ onNavigate }: Props) {
  const { t } = useTranslation('store');
  const { mode, setMode } = useTheme();
  const { data, isLoading, error } = useQuery({
    queryKey: ['store', 'catalog'],
    queryFn: fetchStoreCatalog,
    staleTime: 30_000,
  });
  const itemCount = useCartStore(selectItemCount);

  const groups = useMemo(() => groupCatalog(data ?? []), [data]);
  // Land on Home by default — the highest-intent segment. A ?tier= query
  // (the ICP pages' CTA path, e.g. /store?tier=commercial_ac) overrides;
  // 'all' remains reachable via its chip, now last in the row.
  const [tierFilter, setTierFilter] = useState<StoreProductTier | 'all'>(() => {
    const q = new URLSearchParams(window.location.search).get('tier');
    return q === 'all' || q === 'home' || q === 'commercial_ac' || q === 'accessory'
      ? q
      : 'home';
  });
  const [openFamily, setOpenFamily] = useState<ProductFamily | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const visibleGroups = tierFilter === 'all' ? groups : groups.filter((g) => g.tier === tierFilter);

  return (
    <div
      className={styles.store}
      data-store-theme={mode}
      onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 48)}
    >
      {/* Header — transparent over the hero, solid once scrolled past it. */}
      <header className={`${styles.head} ${scrolled ? styles.headSolid : ''}`}>
        <button type="button" className={styles.headBtn} aria-label={t('header.back')} onClick={() => onNavigate('/profile')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${scrolled ? styles.headTitleShown : ''}`}>{t('header.title')}</span>
        <span className={styles.headActions}>
          <button
            type="button"
            className={styles.headBtn}
            aria-label={mode === 'dark' ? t('header.toLight') : t('header.toDark')}
            onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
          >
            {mode === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <button
            type="button"
            className={styles.headBtn}
            aria-label={t('header.askEvie')}
            onClick={() => {
              useEvieChatStore.getState().setContext({ surface: 'store' });
              useEvieChatStore.getState().openSheet();
            }}
          >
            <EvieOrb size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('header.orders')} onClick={() => onNavigate('/store/orders')}>
            <Package size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('header.cart')} onClick={() => onNavigate('/store/cart')}>
            <ShoppingCart size={18} />
            {itemCount > 0 && <span className={styles.headCartCount}>{itemCount}</span>}
          </button>
        </span>
      </header>

      {/* Hero — the garage still behind a scrim, headline on top. */}
      <section className={styles.hero}>
        <img className={styles.heroBg} src="/media/brand/hero.jpg" alt="" aria-hidden />
        <div className={styles.heroScrim} aria-hidden />
        <div className={styles.heroContent}>
          <span className={styles.heroEyebrow}>EV INITIATIVE</span>
          <h1 className={styles.heroTitle}>
            {t('hero.titleLead')} <span className={styles.heroAccent}>{t('hero.titleAccent')}</span>
          </h1>
          <p className={styles.heroSub}>
            {t('hero.sub')}
          </p>
        </div>
      </section>

      {/* Category chips — tier order first (Home leads, matching the
          default filter), All last as the escape hatch. */}
      {!isLoading && !error && groups.length > 0 && (
        <div className={styles.chips}>
          {groups.map((g) => (
            <button
              key={g.tier}
              type="button"
              className={`${styles.chip} ${tierFilter === g.tier ? styles.chipActive : ''}`}
              onClick={() => setTierFilter(g.tier)}
            >
              {TIER_LABEL[g.tier]}
            </button>
          ))}
          <button
            type="button"
            className={`${styles.chip} ${tierFilter === 'all' ? styles.chipActive : ''}`}
            onClick={() => setTierFilter('all')}
          >
            {t('filter.all')}
          </button>
        </div>
      )}

      <div className={styles.body}>
        {isLoading && (
          <div className={styles.grid}>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </div>
        )}

        {!isLoading && error && (
          <div className={styles.errorBox}>
            {error instanceof Error ? error.message : t('error.catalog')}
          </div>
        )}

        {!isLoading && !error && groups.length === 0 && (
          <div className={styles.emptyBox}>
            <p>{t('empty.catalog')}</p>
            <button type="button" className={styles.ghostBtn} onClick={() => onNavigate('/profile')}>{t('header.back')}</button>
          </div>
        )}

        {!isLoading && !error && visibleGroups.map((group) => (
          <section key={group.tier} className={styles.tierSection}>
            <h2 className={styles.tierHeading}>{TIER_LABEL[group.tier]}</h2>
            <div className={styles.grid}>
              {group.families.map((family) => (
                <button
                  key={family.key}
                  type="button"
                  className={styles.card}
                  onClick={() => setOpenFamily(family)}
                >
                  <div className={styles.cardMedia}>
                    {family.media ? (
                      <img className={styles.cardImg} src={family.media.hero} alt={family.title} loading="lazy" />
                    ) : (
                      <div className={styles.cardImgFallback} aria-hidden />
                    )}
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.cardName}>{family.title}</span>
                    {family.blurb && <span className={styles.cardBlurb}>{family.blurb}</span>}
                    <span className={styles.cardPriceRow}>
                      {hasChoices(family) && <span className={styles.cardFrom}>{t('card.from')}</span>}
                      <span className={styles.cardPrice}>{formatCents(family.fromPriceCents)}</span>
                      <span className={styles.usd}>USD</span>
                      <span className={styles.cardCta}>
                        {hasChoices(family) ? t('card.options') : t('card.view')}
                        <ChevronRight size={14} />
                      </span>
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {/* DC hardware is quote-only and lives on the public site for
                now; the commercial page with real DC filters is slice two. */}
            {group.tier === 'commercial_ac' && (
              <a
                className={styles.dcLink}
                href={shopUrl('/commercial-dc')}
                target="_blank"
                rel="noreferrer"
              >
                {t('commercialDc')} <ChevronRight size={14} />
              </a>
            )}
          </section>
        ))}

        {/* Testimonial row — committed UGC films, autoplay muted loop. */}
        {!isLoading && !error && groups.length > 0 && (
          <section className={styles.testimonials}>
            <h2 className={styles.tierHeading}>{t('testimonials.heading')}</h2>
            <div className={styles.testimonialRow}>
              {TESTIMONIALS.map((t) => (
                <button
                  key={t.src}
                  type="button"
                  className={`${styles.testimonial} ${styles.testimonialTap}`}
                  aria-label={`Why ${t.role.toLowerCase()}s pick EVI`}
                  onClick={() => onNavigate(t.why)}
                >
                  <video
                    className={styles.testimonialVideo}
                    src={t.src}
                    poster={t.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                  <span className={styles.testimonialCap}>{t.role}</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <ProductOptionsSheet family={openFamily} onClose={() => setOpenFamily(null)} onNavigate={onNavigate} />
    </div>
  );
}
